import { cx } from 'class-variance-authority';
import { useFormatter, useTranslations } from 'next-intl';
import { PropsWithChildren, ReactNode } from 'react';

import { STEP } from '@/app/[locale]/(partners)/business/const';
import { usePathname } from '@/i18n/routing';
import { OrderStatus, PaymentMethod } from '@/types/Order';

import AddressDisplay, { type AddressDisplayProps } from '../AddressDisplay';
import EOPTooltip from '../EOPTooltip';
import SectionHeading from '../SectionHeading';

export interface OrderInfoProps {
  readonly className?: string;
  readonly address?: AddressDisplayProps['address'];
  readonly email?: string;
  readonly orderStatus?: OrderStatus;
  readonly paymentMethod?: PaymentMethod;
  readonly details?: {
    readonly number: string;
    readonly date: Date;
    readonly quantity: number;
  };
}

export const Info = ({
  heading,
  children,
}: PropsWithChildren<{ readonly heading?: ReactNode }>) => {
  return heading ? (
    <section
      data-testid="order-info-section"
      className="flex w-48 min-w-min shrink-0 grow flex-col md:w-auto"
    >
      <SectionHeading Element="h3" weight="bold" variant="h6">
        {heading}
      </SectionHeading>
      {children}
    </section>
  ) : (
    <div className="flex w-48 min-w-min shrink-0 grow flex-col md:w-auto">
      {children}
    </div>
  );
};

const OrderInfo = ({
  address,
  email,
  details,
  className,
  orderStatus,
  paymentMethod,
}: OrderInfoProps) => {
  const t = useTranslations();
  const format = useFormatter();
  const currentStep = usePathname();

  // Show tooltip if the order has an address and user
  // still has ability to edit it later.
  const showAddressTooltip =
    Boolean(address?.postal) &&
    orderStatus === OrderStatus.pending &&
    paymentMethod === PaymentMethod.net_terms &&
    currentStep === STEP.ACCOUNT.path;

  if (
    !address &&
    !email &&
    !details?.number &&
    !details?.date &&
    !details?.quantity
  ) {
    return null;
  }

  return (
    <div
      data-testid="order-info"
      className={cx('flex gap-4 flex-wrap justify-between', className)}
    >
      {details?.number && details?.date && details?.quantity ? (
        <Info heading={t('eop_order_number_text', { number: details.number })}>
          <ol>
            <li>
              {format.dateTime(details.date, {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </li>

            <li>
              {t('eop_quantity_text', {
                number: details.quantity,
              })}
            </li>
          </ol>
        </Info>
      ) : null}

      {email && (
        <Info heading={t('eop_order_email')}>
          <p>{email}</p>
        </Info>
      )}

      {address && (
        <Info
          heading={
            <span className="flex items-center gap-1.5">
              {t('eop_order_shippingaddress')}
              {showAddressTooltip && (
                <EOPTooltip
                  title={t('eop_tooltip_edit_address')}
                  aria-label={t('eop_tooltip_aria_address')}
                />
              )}
            </span>
          }
        >
          <AddressDisplay address={address} />
        </Info>
      )}
    </div>
  );
};

export default OrderInfo;
